/** @jsx jsx */
import * as React from "react";
import { css, InterpolationWithTheme, jsx } from "@emotion/core";

import { interventionsSummaryStyles } from "../../styles/Interventions/interventionsSummary";
import { IFetchScheduledInterventionsResponse, INTERVENTIONS_STATUS } from "../../models/IFetchScheduledInterventions";
import { interventionsHistoryStyles } from "../../styles/Interventions/interventionsHistory";
import { interventionsDailyViewStyles } from "../../styles/Interventions/interventionsDailyView";
import { Card } from "../common/Card";
import { InterventionsHistoryMobile } from "./mweb/InterventionDailyView/InterventionHistoryMobile/InterventionHistoryMobile";

interface IIntervantionsHistoryProps {
    historyData: Array<IFetchScheduledInterventionsResponse[]>;
    isMobile: boolean;
}

interface IIntervantionsHistoryState {
    hoverIndex: number;
    sortBy: "STARTDATE" | "ENDDATE";
    orderBy: "ASC" | "DES"
}

export interface IHistoryTableData {
    startDate: number;
    endDate: number;
    activityName: string;
    doneTask: number;
    scheduledTask: number;
    missedTask: number;
    total: number
    taskId?: string
}

export class InterventionsHistory extends React.Component<IIntervantionsHistoryProps, IIntervantionsHistoryState> {
    constructor(props: IIntervantionsHistoryProps) {
        super(props);
        this.state = {
            hoverIndex: -1,
            sortBy: null,
            orderBy: null
        };
        this.setHoverIndex = this.setHoverIndex.bind(this);
        this.setSortStatus = this.setSortStatus.bind(this);
    }
    private parseHistoryData(historyData: Array<IFetchScheduledInterventionsResponse[]>): IHistoryTableData[] {
        let parsedData: IHistoryTableData[] = [];
        if (historyData) {
            historyData = historyData.filter(data => data.length > 0);

            parsedData = historyData.map((historyList, index) => {
                let startDate: number;
                let endDate: number;
                let activityName: string;
                let doneTask: number = 0;
                let scheduledTask: number = 0;
                let missedTask: number = 0;
                const total: number = historyList.length;
                historyList.forEach(d => {
                    if (!startDate || d.date < startDate) {
                        startDate = d.date;
                    }
                    if (!endDate || d.date > endDate) {
                        endDate = d.date;
                    }
                    if (!activityName && d.lineItem.modifiedInterventionText) {
                        activityName = d.lineItem.modifiedInterventionText;
                    }
                    if (d.status === INTERVENTIONS_STATUS.DONE) {
                        doneTask++;
                    }
                    if (d.status === INTERVENTIONS_STATUS.MISSED) {
                        missedTask++;
                    }
                    if (d.status === INTERVENTIONS_STATUS.SCHEDULED) {
                        scheduledTask++;
                    }
                })

                return {
                    startDate,
                    endDate,
                    activityName,
                    doneTask,
                    scheduledTask,
                    missedTask,
                    total,
                    taskId: `activityId-${index}`
                }
            });
        }

        return parsedData;
    }
    setHoverIndex(index: number): void {
        if (this.state.hoverIndex === index) {
            this.setState({ hoverIndex: -1 });
        } else {
            this.setState({ hoverIndex: index });
        }
    }

    private setSortStatus(sortBySelected: "STARTDATE" | "ENDDATE"): void {
        const { sortBy, orderBy } = this.state;
        if (sortBySelected === sortBy) {
            this.setState({ orderBy: orderBy === "ASC" ? "DES" : "ASC" });
        } else {
            this.setState({ sortBy: sortBySelected, orderBy: "ASC" });
        }
    }

    private sortData(data: IHistoryTableData[]): IHistoryTableData[] {
        const { sortBy, orderBy } = this.state;
        if (sortBy && orderBy && data && data.length > 0) {
            if (sortBy === "STARTDATE" && orderBy === "ASC") {
                data = data.sort((tableData1, tableData2) => tableData1.startDate - tableData2.startDate);
            } else if (sortBy === "STARTDATE" && orderBy === "DES") {
                data = data.sort((tableData1, tableData2) => tableData2.startDate - tableData1.startDate);
            } else if (sortBy === "ENDDATE" && orderBy === "ASC") {
                data = data.sort((tableData1, tableData2) => tableData1.endDate - tableData2.endDate);
            } else if (sortBy === "ENDDATE" && orderBy === "DES") {
                data = data.sort((tableData1, tableData2) => tableData2.endDate - tableData1.endDate);
            }
        }
        return data;
    }

    getDesktopDesign = (data: IHistoryTableData[]) => {
        const { hoverIndex } = this.state;

        return (
            <div>
                <table css={interventionsHistoryStyles.table}>
                    <thead>
                        <tr css={interventionsHistoryStyles.tableHeader}>
                            <td css={interventionsHistoryStyles.paddedTd}>
                                <div>Activity</div>
                            </td>
                            <td css={interventionsHistoryStyles.paddedTd} onClick={() => this.setSortStatus("STARTDATE")}>
                                <div>
                                    <span> Start Date</span>
                                    <span css={interventionsHistoryStyles.caratStyles} className="glyphicon glyphicon-chevron-down"></span>
                                </div>
                            </td>
                            <td css={interventionsHistoryStyles.paddedTd} onClick={() => this.setSortStatus(
                                "ENDDATE"
                            )}>
                                <div>
                                    <span>End Date</span>
                                    <span css={interventionsHistoryStyles.caratStyles} className="glyphicon glyphicon-chevron-down"></span>
                                </div>
                            </td>
                            <td css={interventionsHistoryStyles.paddedTd}>
                                <div>Total</div>
                            </td>
                            <td css={interventionsHistoryStyles.paddedTd}>
                                <div>Complete (%)</div>
                            </td>
                            <td css={interventionsHistoryStyles.paddedTd}>
                                <div>Incomplete (%)</div>
                            </td>
                            <td css={interventionsHistoryStyles.paddedTd}>
                                <div> No Response (%)</div>
                            </td>
                        </tr>
                    </thead>
                    <tbody css={interventionsHistoryStyles.tableBodyText}>
                        <tr>
                            <td colSpan={7}>
                                <div css={interventionsDailyViewStyles.divider}></div>
                            </td>
                        </tr>
                        {data && data.map(({ total, startDate, endDate, scheduledTask, doneTask, missedTask, activityName }, idx) => {
                            const completePct: number = Math.floor((doneTask / total) * 100);
                            const incomplete: number = scheduledTask + missedTask;
                            const incompletePct: number = Math.floor((incomplete / total) * 100);
                            const noRes: number = total - (doneTask + incomplete);
                            const noResPct: number = Math.floor((noRes / total) * 100);
                            const hoverCss: InterpolationWithTheme<any> = hoverIndex === idx ? { background: "pink", cursor: "pointer" } : {};
                            const borderHoverStyle = { borderLeft: hoverIndex === idx ? "5px solid #ff316d" : "5px solid transparent" };
                            return (<tr key={idx} css={{ background: (idx % 2) ? "#f8f5f5" : "ffffff", ...hoverCss }} onMouseEnter={() => this.setHoverIndex(idx)} onMouseLeave={() => this.setHoverIndex(idx)}>
                                <td css={{ ...borderHoverStyle, ...interventionsHistoryStyles.paddedTd as any }}>
                                    <div css={interventionsHistoryStyles.colData}>{activityName}</div>
                                </td>
                                <td css={interventionsHistoryStyles.paddedTd}>
                                    <div css={interventionsHistoryStyles.colData}>{new Date(startDate).toLocaleDateString()}</div>
                                </td>
                                <td css={interventionsHistoryStyles.paddedTd}>
                                    <div css={interventionsHistoryStyles.colData}>{new Date(endDate).toLocaleDateString()}</div>
                                </td>
                                <td css={interventionsHistoryStyles.paddedTd}>
                                    <div css={interventionsHistoryStyles.colData}>{total}</div>
                                </td>
                                <td css={interventionsHistoryStyles.paddedTd}>
                                    <div css={interventionsHistoryStyles.colData}>{doneTask} ({completePct})%</div>
                                </td>
                                <td css={interventionsHistoryStyles.paddedTd}>
                                    <div css={interventionsHistoryStyles.colData}>{incomplete} ({incompletePct})%</div>
                                </td>
                                <td css={interventionsHistoryStyles.paddedTd}>
                                    <div css={interventionsHistoryStyles.colData}>{noRes} ({noResPct})%</div>
                                </td>
                            </tr>)
                        })

                        }
                    </tbody>
                </table>
            </div>
        )
    }

    render() {
        const { isMobile } = this.props;
        let data = this.parseHistoryData(this.props.historyData);
        data = this.sortData(data);

        return (<div css={interventionsHistoryStyles.historyCardWrapper}>
            <Card>
                <div css={interventionsSummaryStyles.summaryContainer}>
                    <div css={interventionsSummaryStyles.cardHeaderText}>Intervention History</div>
                    {isMobile ? <InterventionsHistoryMobile data={data} {...this.props} /> : this.getDesktopDesign(data)}
                </div>
                <div css={interventionsHistoryStyles.legendContainer}>
                    <div css={interventionsHistoryStyles.prevNextBtn}>
                        <span className="glyphicon glyphicon-chevron-left"></span>
                        <span>Previous</span>
                    </div>
                    <div css={interventionsHistoryStyles.paginationText}>{"1 - 10 of 30"}</div>
                    <div css={interventionsHistoryStyles.prevNextBtn}>
                        <span>Next</span>
                        <span className="glyphicon glyphicon-chevron-right"></span>
                    </div>
                </div>
            </Card>
        </div >)
    }
}